/**
 * Rules Engine.
 *
 * Pure function: given a transaction's descriptionCanonical + the
 * current set of rules, return the best-matching category (or null
 * for no match). Used at import time and when the user manually
 * re-runs classification.
 *
 * Match precedence:
 *   1. Account-specific rules beat global rules (when both match)
 *   2. Higher priority beats lower priority
 *   3. Within the same priority, first defined wins
 *
 * User overrides are NOT handled here — the caller must check
 * `userCategorised` before overwriting.
 */

import { BankRule, TxScope } from '../types';

export interface ClassificationResult {
  category: string;
  subcategory?: string;
  /** Books assignment carried by the matching rule, when it has one. */
  scope?: TxScope;
  ruleId: string;
}

function ruleMatches(rule: BankRule, descriptionCanonical: string, descriptionRaw: string): boolean {
  if (!rule.enabled) return false;
  const pattern = rule.pattern || '';
  if (!pattern) return false;
  // We match against the raw description for case-sensitive patterns
  // (regex flags decide) and against the canonical (lowercased) form
  // for the simple contains/startsWith helpers.
  const haystack = descriptionCanonical;
  switch (rule.matchType) {
    case 'contains':
      return haystack.includes(pattern.toLowerCase());
    case 'startsWith':
      return haystack.startsWith(pattern.toLowerCase());
    case 'regex': {
      try {
        // Build flag-free regex on raw description so user controls
        // case sensitivity via inline flags `(?i)` if they want.
        return new RegExp(pattern).test(descriptionRaw);
      } catch {
        // Malformed regex — quietly skip rather than crashing import.
        return false;
      }
    }
    default:
      return false;
  }
}

/** Pick the best-matching rule, or null if none match. */
export function classify(
  rules: BankRule[],
  accountId: string,
  descriptionRaw: string,
  descriptionCanonical: string,
): ClassificationResult | null {
  let best: BankRule | null = null;
  let bestScore = -Infinity;

  for (const rule of rules) {
    if (!ruleMatches(rule, descriptionCanonical, descriptionRaw)) continue;
    if (rule.accountId && rule.accountId !== accountId) continue;
    // Score: account-specific +1000, priority direct, definition order
    // breaks ties (rules[] is preserved in input order).
    const score = (rule.accountId === accountId ? 1000 : 0) + (rule.priority || 0);
    if (score > bestScore) {
      best = rule;
      bestScore = score;
    }
  }

  if (!best) return null;
  return {
    category: best.category,
    subcategory: best.subcategory,
    scope: best.scope,
    ruleId: best.id,
  };
}

/** Best-effort merchant/counterparty extraction. Used as a hint in the
 *  UI — never authoritative. Strips ANZ's leading transaction-type
 *  noise so the user sees "COLES ONLINE HAWTHORN EAST" instead of
 *  "VISA DEBIT PURCHASE CARD 9984 COLES ONLINE HAWTHORN EAST". */
export function extractCounterpartyHint(descriptionRaw: string): string {
  let s = descriptionRaw;
  // Strip common prefixes ANZ uses.
  const prefixes = [
    /^VISA DEBIT PURCHASE CARD \d+ /i,
    /^EFTPOS /i,
    /^ANZ MOBILE BANKING PAYMENT \d+ TO /i,
    /^ANZ M-?BANKING FUNDS TFER TRANSFER \d+ /i,
    /^PAYMENT TO /i,
    /^PAYMENT FROM /i,
    /^TRANSFER FROM /i,
    /^TRANSFER TO /i,
  ];
  for (const re of prefixes) {
    if (re.test(s)) {
      s = s.replace(re, '');
      break;
    }
  }
  return s.trim();
}
